import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { HiPlus, HiTrash, HiLockClosed } from "react-icons/hi2";
import { HiX, HiCheck } from "react-icons/hi";
import { HiLink } from "react-icons/hi";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../providers/SubscriptionProvider";
import { activityService } from "../../services/activityService";
import { GlobalActivityService } from "../../services/globalActivityService";
import { sessionService } from "../../services/sessionService";
import {
  ActivityType,
  ActivityVariable,
  ActivityVariableDefinition,
  GlobalActivityType,
} from "../../types/activity";
import { PerformanceActivity, PerformanceLog, TrainingLog } from "../../types/post";
import { getUnitsForVariable, getDefaultUnit } from "../../utils/unitUtils";
import {
  parseTimeStringToSeconds,
  formatSecondsToTimeString,
  unitToSeconds,
  secondsToUnitValue,
} from "../../utils/timeDurationUtils";
import { evaluatePersonalBest } from "../../utils/personalBestPredictor";
import { CreateActivityTypeDialog } from "../sessions";
import {
  getDayPostReferences,
  getTrainingLogs,
  getTrainingLog,
  getAllPerformanceLogs,
  updatePerformanceLog,
} from "../../services/dayPostService";
import TrainingLogForm from "./training-log/TrainingLogForm";

interface AddPerformanceLogFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (performanceLog: {
    notes: string;
    performanceExpectation: number;
    performanceResult: string;
    performanceTitle: string;
    performanceDate: string;
    activity?: PerformanceActivity;
    linkedSessionID?: string;
    linkedTrainingLogID?: string;
    tag?: string[];
  }) => Promise<string> | void;
  dateID: string;
  editMode?: boolean;
  initialData?: {
    notes: string;
    performanceExpectation: number;
    performanceResult: string;
    performanceTitle?: string;
    performanceDate?: string;
    activity?: PerformanceActivity;
    linkedSessionID?: string;
    linkedTrainingLogID?: string;
    tag?: string[];
  };
  logId?: string;
  /** Override the overlay z-index when stacking above another modal (e.g. training log form). */
  overlayZIndexClass?: string;
  /** When provided in edit mode, renders a delete action inside the modal. */
  onDelete?: () => void;
}

const AddPerformanceLogForm: React.FC<AddPerformanceLogFormProps> = ({
  open,
  onClose,
  onSubmit,
  dateID,
  editMode = false,
  initialData,
  logId,
  overlayZIndexClass = "z-[9999]",
  onDelete,
}) => {
  const { currentUser, userData, authState } = useAuth();
  const { isPremium } = useSubscription();
  const isImperial = authState.user?.isImperial ?? false;

  // Personal Best Predictor (Premium+): the athlete's full performance log
  // history, fetched once per open so a just-entered result can be compared
  // against past attempts for the same activity/variable.
  const [pastPerformanceLogs, setPastPerformanceLogs] = useState<
    (PerformanceLog & { id: string })[]
  >([]);
  // historyReady stays false until getAllPerformanceLogs settles. An in-flight
  // empty array must not be treated as "no history" (that would auto-flag PB).
  const [historyReady, setHistoryReady] = useState(false);
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);

  // Convert between a YYYYMMDD dateID and a YYYY-MM-DD <input type="date"> value
  const dateIDToInputValue = (id: string): string => {
    if (!id || id.length < 8) return "";
    return `${id.substring(0, 4)}-${id.substring(4, 6)}-${id.substring(6, 8)}`;
  };
  const inputValueToDateID = (val: string): string => val.replace(/-/g, "");

  // Single date of performance (may differ from when it was logged)
  const [performanceDateInput, setPerformanceDateInput] = useState<string>(
    dateIDToInputValue(initialData?.performanceDate || dateID),
  );
  const dateInputRef = useRef<HTMLInputElement>(null);

  const [performanceExpectation, setPerformanceExpectation] = useState(
    initialData?.performanceExpectation || 5,
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [isPersonalBest, setIsPersonalBest] = useState(
    initialData?.tag?.includes("PB") || false,
  );
  const [isSeasonsBest, setIsSeasonsBest] = useState(
    initialData?.tag?.includes("SB") || false,
  );
  const [isTesting, setIsTesting] = useState(
    initialData?.tag?.includes("Testing") || false,
  );

  // Activity selection state
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalPackActivityTypes, setGlobalPackActivityTypes] = useState<
    ActivityType[]
  >([]);
  const [selectedActivityType, setSelectedActivityType] =
    useState<ActivityType | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [loadingActivityTypes, setLoadingActivityTypes] =
    useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Activity measurement state (for the single activity)
  const [activityVariables, setActivityVariables] = useState<
    ActivityVariable[]
  >([]);
  // Which variables (by index) the user has chosen to record
  const [selectedVarIndices, setSelectedVarIndices] = useState<Set<number>>(
    new Set(),
  );

  // Linked session state (for edit mode)
  const [linkedSession, setLinkedSession] = useState<any>(null);
  const [loadingSession, setLoadingSession] = useState<boolean>(false);

  // State for unit dropdown visibility
  const [activeUnitDropdown, setActiveUnitDropdown] = useState<string | null>(
    null,
  );

  // State for create activity dialog
  const [isCreateActivityDialogOpen, setIsCreateActivityDialogOpen] =
    useState<boolean>(false);

  // Training log linking state
  const [existingTrainingLogs, setExistingTrainingLogs] = useState<
    (TrainingLog & { id: string })[]
  >([]);
  const [loadingTrainingLogs, setLoadingTrainingLogs] =
    useState<boolean>(false);
  const [selectedTrainingLogID, setSelectedTrainingLogID] = useState<
    string | null
  >(initialData?.linkedTrainingLogID || null);
  const [showTrainingLogSelector, setShowTrainingLogSelector] =
    useState<boolean>(false);
  const [showCreateTrainingLogForm, setShowCreateTrainingLogForm] =
    useState<boolean>(false);

  // Post-submit linking state
  const [postSubmitStep, setPostSubmitStep] = useState<boolean>(false);
  const [submittedPerfLogId, setSubmittedPerfLogId] = useState<string | null>(
    null,
  );

  // Time input display strings (keyed by variable index)
  const [timeDisplayValues, setTimeDisplayValues] = useState<
    Record<number, string>
  >({});

  // Check if a variable is a time variable
  const isTimeVariable = (variableName: string): boolean => {
    return variableName.toLowerCase() === "time";
  };

  // Rest gets the same dynamic entry as Time, but (unlike Time) it stores a
  // measurement + unit pair, so it needs converting through that unit rather
  // than always being raw seconds.
  const isRestVariable = (variableName: string): boolean => {
    return variableName.toLowerCase() === "rest";
  };

  // Dynamic athletics-style time entry: "30.30" (seconds), "1:08.90" (m:s),
  // "2:30:32" (h:m:s) - shape is read from the string, not fixed fields.
  const secondsToTimeDisplay = formatSecondsToTimeString;
  const timeDisplayToSeconds = parseTimeStringToSeconds;

  const formatDate = (id: string) => {
    const year = parseInt(id.substring(0, 4));
    const month = parseInt(id.substring(4, 6));
    const day = parseInt(id.substring(6, 8));
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Stringify the pack IDs for a stable dependency comparison
  const userPackIdsString = JSON.stringify(userData?.adminActivityCatIDs || []);

  // Fetch activity types (user activities + global pack activities)
  useEffect(() => {
    const fetchAllActivityTypes = async () => {
      if (!currentUser?.uid || !open) return;

      try {
        setLoadingActivityTypes(true);
        const types = await activityService.fetchActivityTypes(currentUser.uid);
        setActivityTypes(types);

        const userPackIds: string[] = JSON.parse(userPackIdsString);
        if (userPackIds.length > 0) {
          try {
            const globalTypes =
              await GlobalActivityService.fetchActivityTypesForCategories(
                userPackIds,
              );

            const convertedTypes: ActivityType[] = globalTypes.map(
              (gt: GlobalActivityType) => ({
                id: `global_${gt.categoryId}_${gt.id}`,
                name: gt.name,
                description: gt.description,
                variables: gt.variables.map((v): ActivityVariableDefinition => {
                  if (typeof v === "string") {
                    return {
                      name: v,
                      defaultUnit: getDefaultUnit(v, isImperial),
                    };
                  }
                  return {
                    ...v,
                    defaultUnit: getDefaultUnit(v.name, isImperial),
                  };
                }),
                image: gt.image,
                isFromPack: true,
                categoryId: gt.categoryId,
              }),
            );
            setGlobalPackActivityTypes(convertedTypes);
          } catch (packError) {
            console.error(
              "Error fetching pack activity types:",
              packError,
            );
            setGlobalPackActivityTypes([]);
          }
        } else {
          setGlobalPackActivityTypes([]);
        }
      } catch (error) {
        console.error("Error fetching activity types:", error);
      } finally {
        setLoadingActivityTypes(false);
      }
    };

    fetchAllActivityTypes();
  }, [currentUser?.uid, open, userPackIdsString, isImperial]);

  // Combine user activities with global pack activities (de-duped by name)
  const allActivityTypes = useMemo(() => {
    const userActivityNames = new Set(
      activityTypes.map((a) => a.name.toLowerCase()),
    );
    const filteredPackActivities = globalPackActivityTypes.filter(
      (ga) => !userActivityNames.has(ga.name.toLowerCase()),
    );
    return [...activityTypes, ...filteredPackActivities];
  }, [activityTypes, globalPackActivityTypes]);

  // Fetch existing training logs for this date
  useEffect(() => {
    const fetchTrainingLogs = async () => {
      if (!currentUser?.uid || !open || !dateID) return;

      try {
        setLoadingTrainingLogs(true);
        const refs = await getDayPostReferences(currentUser.uid, dateID);
        if (refs?.trainingLogIDs && refs.trainingLogIDs.length > 0) {
          const logs = await getTrainingLogs(
            currentUser.uid,
            refs.trainingLogIDs,
          );
          setExistingTrainingLogs(logs);
        } else {
          setExistingTrainingLogs([]);
        }
      } catch (error) {
        console.error("Error fetching training logs:", error);
        setExistingTrainingLogs([]);
      } finally {
        setLoadingTrainingLogs(false);
      }
    };

    fetchTrainingLogs();
  }, [currentUser?.uid, open, dateID]);

  // Fetch performance log history for the PB Predictor (Premium+ only).
  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      if (!currentUser?.uid || !open || !isPremium) {
        setPastPerformanceLogs([]);
        setHistoryReady(false);
        setHistoryLoadFailed(false);
        return;
      }
      setHistoryReady(false);
      setHistoryLoadFailed(false);
      try {
        const logs = await getAllPerformanceLogs(currentUser.uid);
        if (cancelled) return;
        setPastPerformanceLogs(logs);
        setHistoryLoadFailed(false);
        setHistoryReady(true);
      } catch (error) {
        console.error("Error fetching performance log history:", error);
        if (cancelled) return;
        setPastPerformanceLogs([]);
        setHistoryLoadFailed(true);
        setHistoryReady(true);
      }
    };
    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, open, isPremium]);

  // Initialize the activity from a new-style log's denormalized `activity` field
  useEffect(() => {
    if (!open || !editMode || !initialData?.activity) return;

    const { activityTypeID, activityName, variables } = initialData.activity;
    setSelectedActivityType({
      id: activityTypeID,
      name: activityName,
      description: "",
      variables: variables.map((v) => v.name),
    });
    setActivityVariables(variables);
    setSelectedVarIndices(new Set(variables.map((_, i) => i)));
    setSearchTerm(activityName);

    const timeDisplays: Record<number, string> = {};
    variables.forEach((v, i) => {
      if (isTimeVariable(v.name) && v.measurement !== null) {
        timeDisplays[i] = secondsToTimeDisplay(v.measurement);
      } else if (isRestVariable(v.name) && v.measurement !== null) {
        timeDisplays[i] = secondsToTimeDisplay(
          unitToSeconds(v.measurement, v.unit),
        );
      }
    });
    setTimeDisplayValues(timeDisplays);
  }, [open, editMode, initialData?.activity]);

  // Fetch linked session in edit mode (legacy logs only - new logs use `activity`)
  useEffect(() => {
    const fetchLinkedSession = async () => {
      if (
        !currentUser?.uid ||
        !open ||
        !editMode ||
        !initialData?.linkedSessionID ||
        initialData?.activity
      )
        return;

      try {
        setLoadingSession(true);
        const session = await sessionService.getSession(
          currentUser.uid,
          initialData.linkedSessionID,
        );
        setLinkedSession(session);

        // If session has activity measurements, set the first one
        if (
          session?.ActivityMeasurementList &&
          session.ActivityMeasurementList.length > 0
        ) {
          const firstActivity = session.ActivityMeasurementList[0];
          const activityType = activityTypes.find(
            (t) => t.id === firstActivity.ActivityTypeID,
          );

          if (activityType) {
            setSelectedActivityType(activityType);
            setActivityVariables(firstActivity.variables);
            setSelectedVarIndices(
              new Set(firstActivity.variables.map((_, i: number) => i)),
            );
            // Initialize time display values for time variables
            const timeDisplays: Record<number, string> = {};
            firstActivity.variables.forEach(
              (v: ActivityVariable, i: number) => {
                if (isTimeVariable(v.name) && v.measurement !== null) {
                  timeDisplays[i] = secondsToTimeDisplay(v.measurement);
                } else if (isRestVariable(v.name) && v.measurement !== null) {
                  timeDisplays[i] = secondsToTimeDisplay(
                    unitToSeconds(v.measurement, v.unit),
                  );
                }
              },
            );
            setTimeDisplayValues(timeDisplays);
          }
        }
      } catch (error) {
        console.error("Error fetching linked session:", error);
      } finally {
        setLoadingSession(false);
      }
    };

    fetchLinkedSession();
  }, [
    currentUser?.uid,
    open,
    editMode,
    initialData?.linkedSessionID,
    activityTypes,
  ]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter activity types based on search term
  const filteredActivityTypes = allActivityTypes.filter((activityType) => {
    const searchLower = searchTerm.toLowerCase();
    return activityType.name.toLowerCase().includes(searchLower);
  });

  // Handle activity selection
  const handleActivitySelect = (activityType: ActivityType) => {
    setSelectedActivityType(activityType);
    setSearchTerm(activityType.name);
    setShowDropdown(false);

    // Initialize variables for this activity
    const initialVariables: ActivityVariable[] = activityType.variables.map(
      (v) => {
        const varName = typeof v === "string" ? v : v.name;
        const varUnit = typeof v === "string" ? "" : v.defaultUnit || "";
        return {
          name: varName,
          measurement: null,
          unit: varUnit,
        };
      },
    );
    setActivityVariables(initialVariables);
    // Default: record all variables; user can deselect any they don't want
    setSelectedVarIndices(new Set(initialVariables.map((_, i) => i)));
    setTimeDisplayValues({}); // Reset time displays for new activity
  };

  // Toggle whether a variable is recorded
  const toggleVariableSelected = (index: number) => {
    setSelectedVarIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Clear the selected activity to choose a different one
  const handleClearActivity = () => {
    setSelectedActivityType(null);
    setSearchTerm("");
    setActivityVariables([]);
    setSelectedVarIndices(new Set());
    setTimeDisplayValues({});
  };

  // Handle time variable display change
  const handleTimeDisplayChange = (index: number, display: string) => {
    setTimeDisplayValues((prev) => ({ ...prev, [index]: display }));
    const seconds = timeDisplayToSeconds(display);
    const updatedVariables = [...activityVariables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      measurement: seconds,
    };
    setActivityVariables(updatedVariables);
  };

  // Handle Rest display change - unlike Time, Rest keeps its existing unit
  // (sec/min), so the typed value is converted back into that unit rather
  // than stored as raw seconds. Saved shape (measurement + unit) is unchanged.
  const handleRestDisplayChange = (index: number, display: string) => {
    setTimeDisplayValues((prev) => ({ ...prev, [index]: display }));
    const seconds = timeDisplayToSeconds(display);
    const unit = activityVariables[index]?.unit;
    const updatedVariables = [...activityVariables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      measurement: secondsToUnitValue(seconds, unit),
    };
    setActivityVariables(updatedVariables);
  };

  // Handle variable value change
  const handleVariableChange = (index: number, value: number | null) => {
    const updatedVariables = [...activityVariables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      measurement: value,
    };
    setActivityVariables(updatedVariables);
  };

  // Handle variable unit change
  const handleUnitChange = (index: number, unit: string) => {
    const updatedVariables = [...activityVariables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      unit: unit,
    };
    setActivityVariables(updatedVariables);
  };

  // Whether there's a recorded variable to evaluate at all (independent of
  // tier) — used to decide whether to show the Premium upsell teaser below.
  const hasMeasurementToEvaluate = useMemo(
    () =>
      !!selectedActivityType?.id &&
      activityVariables.some(
        (v, i) => selectedVarIndices.has(i) && v.measurement != null,
      ),
    [selectedActivityType?.id, activityVariables, selectedVarIndices],
  );

  // Personal Best Predictor (Premium+): evaluate the first recorded variable
  // with a value against this athlete's own history for the same activity.
  const personalBestEvaluation = useMemo(() => {
    // Wait for history to settle. A failed fetch is "unknown" (skip), not
    // "no history" (which would falsely auto-flag a first PB).
    if (
      !isPremium ||
      !selectedActivityType?.id ||
      !historyReady ||
      historyLoadFailed
    ) {
      return null;
    }
    const primaryIndex = activityVariables.findIndex(
      (v, i) => selectedVarIndices.has(i) && v.measurement != null,
    );
    if (primaryIndex === -1) return null;
    const primaryVariable = activityVariables[primaryIndex];
    if (primaryVariable.measurement == null) return null;

    return {
      variableName: primaryVariable.name,
      ...evaluatePersonalBest(
        pastPerformanceLogs,
        selectedActivityType.id,
        primaryVariable.name,
        primaryVariable.measurement,
        primaryVariable.unit,
        { excludeLogId: editMode ? logId : undefined },
      ),
    };
  }, [
    isPremium,
    selectedActivityType?.id,
    activityVariables,
    selectedVarIndices,
    pastPerformanceLogs,
    historyReady,
    historyLoadFailed,
    editMode,
    logId,
  ]);

  // Auto-flag Personal Best when the predictor detects one. If the athlete
  // unchecks PB, keep it off until the measurement/activity changes.
  const autoFlaggedPbRef = useRef(false);
  const pbDismissedRef = useRef(false);
  const pbEvalKeyRef = useRef("");
  const pbEvalKey = personalBestEvaluation
    ? `${selectedActivityType?.id}|${personalBestEvaluation.variableName}|${personalBestEvaluation.isNewPersonalBest}|${personalBestEvaluation.previousBest}`
    : "";

  useEffect(() => {
    if (pbEvalKey !== pbEvalKeyRef.current) {
      // New measurement context — allow auto-flag again.
      if (
        pbEvalKey.split("|").slice(0, 2).join("|") !==
        pbEvalKeyRef.current.split("|").slice(0, 2).join("|")
      ) {
        pbDismissedRef.current = false;
      }
      pbEvalKeyRef.current = pbEvalKey;
    }
  }, [pbEvalKey]);

  useEffect(() => {
    if (!personalBestEvaluation) return;
    if (personalBestEvaluation.isNewPersonalBest) {
      if (!pbDismissedRef.current && !isPersonalBest) {
        setIsPersonalBest(true);
        autoFlaggedPbRef.current = true;
      }
    } else if (autoFlaggedPbRef.current) {
      setIsPersonalBest(false);
      autoFlaggedPbRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalBestEvaluation?.isNewPersonalBest, pbEvalKey]);

  // Handle create activity
  const handleCreateActivity = () => {
    setShowDropdown(false);
    setIsCreateActivityDialogOpen(true);
  };

  // Handle activity type created
  const handleActivityTypeCreated = async (
    activityTypeId: string,
    activityTypeName: string,
  ) => {
    // Refresh activity types list
    try {
      const types = await activityService.fetchActivityTypes(currentUser!.uid);
      setActivityTypes(types);

      // Find the newly created activity in the refreshed list
      const newActivity = types.find(
        (activity) => activity.id === activityTypeId,
      );

      if (newActivity) {
        // Clear the current search state first
        setSearchTerm("");
        setShowDropdown(false);

        // Select the newly created activity
        handleActivitySelect(newActivity);
      }
    } catch (error) {
      console.error("Error refreshing activity types:", error);
    }
  };

  // Handle creating a new training log from within the performance form
  // The new training log form persists the log itself; once saved we fetch it
  // to add to the local list and select it for linking.
  const handleCreateTrainingLogSubmit = useCallback(
    async (trainingLogId: string): Promise<void> => {
      if (!currentUser?.uid) return;

      try {
        const created = await getTrainingLog(currentUser.uid, trainingLogId);
        if (created) {
          setExistingTrainingLogs((prev) => [
            ...prev,
            { ...created, id: trainingLogId } as TrainingLog & { id: string },
          ]);
        }
        setSelectedTrainingLogID(trainingLogId);
        setShowCreateTrainingLogForm(false);
        setShowTrainingLogSelector(false);
      } catch (error) {
        console.error("Error loading created training log:", error);
      }
    },
    [currentUser?.uid],
  );

  // Format duration for display
  const formatDuration = (minutes: number): string => {
    if (!minutes || minutes <= 0) return "";
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}min`;
  };

  // Handle linking a training log after performance log is submitted
  const handlePostSubmitLink = useCallback(
    async (trainingLogID: string) => {
      if (!currentUser?.uid || !submittedPerfLogId) return;
      try {
        await updatePerformanceLog(currentUser.uid, submittedPerfLogId, {
          linkedTrainingLogID: trainingLogID,
        });
        console.log(
          `Linked training log ${trainingLogID} to performance log ${submittedPerfLogId}`,
        );
        handleClose();
      } catch (error) {
        console.error("Error linking training log to performance:", error);
      }
    },
    [currentUser?.uid, submittedPerfLogId],
  );

  // Handle creating a new training log in the post-submit step. The new form
  // persists the log; we just link it to the performance log we just created.
  const handlePostSubmitCreateTrainingLog = useCallback(
    async (trainingLogId: string): Promise<void> => {
      if (!currentUser?.uid) return;

      try {
        if (submittedPerfLogId) {
          await updatePerformanceLog(currentUser.uid, submittedPerfLogId, {
            linkedTrainingLogID: trainingLogId,
          });
        }
        handleClose();
      } catch (error) {
        console.error("Error linking created training log:", error);
      }
    },
    [currentUser?.uid, submittedPerfLogId],
  );

  const handleSubmit = async () => {
    // Validate: an activity must be selected
    if (!selectedActivityType) return;
    if (!currentUser?.uid) return;

    setIsSubmitting(true);
    try {
      // Only record the variables the user has chosen
      const selectedVariables = activityVariables.filter((_, i) =>
        selectedVarIndices.has(i),
      );

      // Build the activity object (source of truth)
      const activity: PerformanceActivity | undefined = selectedActivityType?.id
        ? {
            activityTypeID: selectedActivityType.id,
            activityName: selectedActivityType.name,
            variables: selectedVariables,
            ...(selectedActivityType.categoryId && {
              globalCategoryID: selectedActivityType.categoryId,
            }),
          }
        : undefined;

      // Build tag array based on checkboxes
      const tags: string[] = [];
      if (isPersonalBest) tags.push("PB");
      if (isSeasonsBest) tags.push("SB");
      if (isTesting) tags.push("Testing");

      const performanceDate =
        inputValueToDateID(performanceDateInput) || dateID;

      // Submit performance log (no linkedTrainingLogID yet - linked post-submit)
      const result = await onSubmit({
        performanceTitle: "",
        performanceExpectation,
        performanceResult: "",
        notes: notes.trim(),
        performanceDate,
        ...(activity && { activity }),
        // Always send tags (even an empty array) so edits that remove every
        // tag are persisted rather than merged away by the partial update.
        tag: tags,
      });

      // If in create mode, show post-submit linking step
      if (!editMode && result) {
        setSubmittedPerfLogId(result);
        setPostSubmitStep(true);
      } else {
        handleClose();
      }
    } catch (error) {
      console.error("Error adding performance log:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (editMode && initialData) {
      setPerformanceExpectation(initialData.performanceExpectation);
      setNotes(initialData.notes);
      setPerformanceDateInput(
        dateIDToInputValue(initialData.performanceDate || dateID),
      );
    } else {
      setPerformanceExpectation(5);
      setNotes("");
      setPerformanceDateInput(dateIDToInputValue(dateID));
    }
    // Reset activity selection
    setSelectedActivityType(null);
    setSearchTerm("");
    setActivityVariables([]);
    setSelectedVarIndices(new Set());
    setShowDropdown(false);
    // Reset training log selection
    setSelectedTrainingLogID(null);
    setShowTrainingLogSelector(false);
    setShowCreateTrainingLogForm(false);
    // Reset post-submit state
    setPostSubmitStep(false);
    setSubmittedPerfLogId(null);
    onClose();
  };

  // Update form values when initial data changes
  React.useEffect(() => {
    if (editMode && initialData) {
      setPerformanceExpectation(initialData.performanceExpectation);
      setNotes(initialData.notes);
      setPerformanceDateInput(
        dateIDToInputValue(initialData.performanceDate || dateID),
      );
      // Keep the tag checkboxes in sync with the log being edited so they
      // display the current tags and edits persist correctly.
      setIsPersonalBest(initialData.tag?.includes("PB") || false);
      setIsSeasonsBest(initialData.tag?.includes("SB") || false);
      setIsTesting(initialData.tag?.includes("Testing") || false);
    } else if (!editMode) {
      setPerformanceExpectation(5);
      setNotes("");
      setPerformanceDateInput(dateIDToInputValue(dateID));
      setSelectedActivityType(null);
      setSearchTerm("");
      setActivityVariables([]);
      setSelectedVarIndices(new Set());
      setSelectedTrainingLogID(null);
      setShowTrainingLogSelector(false);
      setIsPersonalBest(false);
      setIsSeasonsBest(false);
      setIsTesting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, initialData, open]);

  // Colour for a 1–10 performance rating (red → yellow → green).
  const getPerformanceColor = (value: number): string => {
    const colorScale = [
      "#dc2626", // 1 - red (worst performance)
      "#ef4444",
      "#f87171",
      "#fca5a5",
      "#fde68a", // 5 - yellow (average)
      "#d9f99d",
      "#a7f3d0",
      "#6ee7b7",
      "#4ade80",
      "#22c55e", // 10 - green (best performance)
    ];
    return colorScale[value - 1] || "#22c55e";
  };

  const getSliderBackground = (value: number): string => {
    const percent = ((value - 1) / 9) * 100; // converts 1–10 to 0–100%
    const color = getPerformanceColor(value);
    // Match the training log slider's neutral track colour.
    const track = "rgba(120,120,120,0.22)";
    return `linear-gradient(to right, ${color} 0%, ${color} ${percent}%, ${track} ${percent}%, ${track} 100%)`;
  };

  const getPerformanceResponse = (rating: number): string => {
    const responses = [
      "Very poor",
      "Poor",
      "Below average",
      "Fair",
      "Average",
      "Above average",
      "Good",
      "Very good",
      "Excellent",
      "Outstanding",
    ];
    return responses[rating - 1] || "Select a rating...";
  };

  // Open the native date picker for the performance date
  const openDatePicker = () => {
    const el = dateInputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      } else {
        el.focus();
      }
    } catch {
      el.focus();
    }
  };

  if (!open) return null;

  const displayDateID = inputValueToDateID(performanceDateInput) || dateID;

  return (
    <div
      className={`fixed inset-0 ${overlayZIndexClass} bg-black/40 backdrop-blur-sm overflow-y-auto`}
      style={{ transform: "translateZ(0)" }}
    >
      <div className="flex min-h-screen md:items-center md:justify-center items-start justify-center">
        <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-none md:rounded-md bg-base-100 text-left align-middle shadow-xl min-h-screen md:min-h-0 md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-start p-6 flex-shrink-0">
            <div>
              <div className="flex items-center gap-1">
                <h2 className="text-lg font-grotesk">
                  {editMode ? "Edit Performance" : "Add Performance"}
                </h2>
                <button
                  onClick={() => setShowInfoDialog(true)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                  type="button"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-base-content/50 hover:text-base-content/80 text-xs font-bold">
                    ?
                  </span>
                </button>
              </div>
              {/* Clickable date — opens a native date picker */}
              <div className="relative mt-1">
                <button
                  type="button"
                  onClick={openDatePicker}
                  className="flex items-center gap-1.5 text-sm text-base-content/60 hover:text-base-content transition-colors"
                >
                  <span className="font-medium underline decoration-dotted underline-offset-4">
                    {formatDate(displayDateID)}
                  </span>
                </button>
                <input
                  ref={dateInputRef}
                  id="performanceDate"
                  type="date"
                  value={performanceDateInput}
                  onChange={(e) => setPerformanceDateInput(e.target.value)}
                  className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </div>
            </div>
            <button
              onClick={handleClose}
              className="bg-base-200 p-2 rounded-full"
            >
              <HiX className="w-5 h-5 text-base-content/80" />
            </button>
          </div>

          {postSubmitStep ? (
            /* Post-Submit: Link Training Log Modal */
            <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold font-grotesk text-base-content">
                  Performance Logged
                </h3>
                <p className="text-sm text-base-content/60 font-grotesk">
                  Link a training session to this performance?
                </p>
              </div>

              <div className="w-full max-w-sm space-y-3">
                {/* Link Existing */}
                <button
                  type="button"
                  onClick={() => setShowTrainingLogSelector(true)}
                  className="w-full flex items-center justify-center gap-2 p-4 bg-base-200 hover:bg-base-300 rounded-md transition-colors text-sm font-medium"
                >
                  <HiLink className="w-4 h-4" />
                  <span>
                    {loadingTrainingLogs
                      ? "Loading..."
                      : existingTrainingLogs.length > 0
                        ? "Link Existing Training Log"
                        : "No Training Logs Yet"}
                  </span>
                </button>

                {/* New Training Log */}
                <button
                  type="button"
                  onClick={() => setShowCreateTrainingLogForm(true)}
                  className="w-full flex items-center justify-center gap-2 p-4 bg-base-200 hover:bg-base-300 rounded-md transition-colors text-sm font-medium"
                >
                  <HiPlus className="w-4 h-4" />
                  <span>Log New Training Session</span>
                </button>

                {/* Training log selector list */}
                {showTrainingLogSelector && (
                  <div className="space-y-2 mt-2">
                    {loadingTrainingLogs ? (
                      <div className="flex items-center justify-center p-4">
                        <span className="loading loading-spinner loading-sm mr-2"></span>
                        <span className="text-sm text-base-content/60">
                          Loading training logs...
                        </span>
                      </div>
                    ) : existingTrainingLogs.length === 0 ? (
                      <div className="text-center p-4">
                        <p className="text-sm text-base-content/60 mb-3">
                          No training logs for this date
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setShowTrainingLogSelector(false);
                            setShowCreateTrainingLogForm(true);
                          }}
                          className="text-sm text-primary font-semibold"
                        >
                          Create one now
                        </button>
                      </div>
                    ) : (
                      <>
                        {existingTrainingLogs.map((log) => (
                          <button
                            key={log.id}
                            type="button"
                            onClick={() => handlePostSubmitLink(log.id)}
                            className="w-full flex items-center justify-between p-3 bg-base-200 hover:bg-base-300 rounded-md transition-colors text-left"
                          >
                            <div>
                              <p className="text-sm font-sans font-semibold text-base-content">
                                {log.tag?.length > 0
                                  ? log.tag.join(", ")
                                  : "Training"}
                              </p>
                              <p className="text-xs text-base-content/60">
                                {formatDuration(log.duration)} · RPE {log.rpe} ·
                                Load {log.load}
                              </p>
                            </div>
                            <HiLink className="w-4 h-4 text-base-content/40" />
                          </button>
                        ))}
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowTrainingLogSelector(false)}
                      className="w-full text-center text-xs text-base-content/50 hover:text-base-content/80 py-1"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Skip */}
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full text-center text-sm text-base-content/50 hover:text-base-content/80 py-2 transition-colors"
                >
                  Skip
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Main Form Content */}
              <div
                className="flex-1 overflow-y-scroll min-h-0"
                style={{
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  touchAction: "pan-y",
                }}
              >
                {/* Activity Search - shown until an activity is selected */}
                {!selectedActivityType && (
                  <div className="px-6 pb-3 pt-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm text-base-content font-sans font-bold">
                        Activity
                      </h4>
                      <span className="text-xs text-base-content/50 font-sans">
                        Search activities &amp; packs
                      </span>
                    </div>

                    {loadingActivityTypes ? (
                      <div className="text-sm text-base-content/60 font-grotesk">
                        Loading activities...
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <div className="relative flex-1" ref={dropdownRef}>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => {
                              setShowDropdown(true);
                            }}
                            placeholder={`Search ${allActivityTypes.length} activities...`}
                            className="w-full px-4 py-3 rounded-md bg-base-200 border border-base-300 focus:border-base-300 focus:outline-none focus:ring-0 text-[16px] font-medium placeholder:text-base-content/40"
                          />

                          {/* Dropdown */}
                          {showDropdown && filteredActivityTypes.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
                              {filteredActivityTypes.map((activityType) => (
                                <button
                                  key={activityType.id}
                                  type="button"
                                  onClick={() =>
                                    handleActivitySelect(activityType)
                                  }
                                  className="w-full text-left px-3 py-2 hover:bg-base-200 focus:bg-base-200 focus:outline-none"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium text-base-content font-sans">
                                      {activityType.name}
                                    </span>
                                    {activityType.isFromPack && (
                                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-base-200 text-base-content/50 font-geist">
                                        Pack
                                      </span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* No results message */}
                          {showDropdown &&
                            searchTerm &&
                            filteredActivityTypes.length === 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-base-100 border border-base-200 rounded-md shadow-lg p-3 text-sm text-base-content/60 font-sans">
                                No activity types found matching "{searchTerm}"
                              </div>
                            )}
                        </div>

                        {/* Only offer to create an activity when the search has no matches */}
                        {searchTerm && filteredActivityTypes.length === 0 && (
                          <button
                            type="button"
                            onClick={handleCreateActivity}
                            className="flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-neutral rounded-md hover:bg-neutral transition-colors font-sans whitespace-nowrap"
                          >
                            <HiPlus className="text-sm" />
                            Create
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected Activity - choose which variables to record */}
                {selectedActivityType && (
                  <div className="px-6 pb-3 pt-2">
                    {/* Change activity */}
                    <button
                      type="button"
                      onClick={handleClearActivity}
                      className="text-xs text-base-content/50 hover:text-base-content/80 font-sans mb-2 transition-colors"
                    >
                      ← Change activity
                    </button>

                    <div className="mb-1">
                      <h4 className="text-2xl text-base-content font-sans font-bold">
                        {selectedActivityType.name}
                      </h4>
                    </div>

                    {editMode && loadingSession ? (
                      <div className="text-sm text-base-content/60 font-sans">
                        Loading...
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        {activityVariables.map((variable, index) => {
                          const checked = selectedVarIndices.has(index);
                          const currentUnit = variable.unit;
                          const availableUnits = getUnitsForVariable(
                            variable.name,
                          );

                          return (
                            <div
                              key={index}
                              className={`flex items-center gap-3 py-1 transition-opacity ${
                                checked ? "" : "opacity-40"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleVariableSelected(index)}
                                className="checkbox checkbox-sm bg-base-100 border-2 border-base-300 checked:bg-neutral checked:border-base-content flex-shrink-0"
                              />
                              <span className="text-sm font-sans font-bold text-base-content flex-1 min-w-0 truncate">
                                {variable.name}
                              </span>
                              <div className="relative w-40 flex-shrink-0">
                                <input
                                  type={
                                    isTimeVariable(variable.name) ||
                                    isRestVariable(variable.name)
                                      ? "text"
                                      : "number"
                                  }
                                  disabled={!checked}
                                  value={
                                    isTimeVariable(variable.name)
                                      ? (timeDisplayValues[index] ??
                                        (variable.measurement !== null
                                          ? secondsToTimeDisplay(
                                              variable.measurement,
                                            )
                                          : ""))
                                      : isRestVariable(variable.name)
                                        ? (timeDisplayValues[index] ??
                                          (variable.measurement !== null
                                            ? secondsToTimeDisplay(
                                                unitToSeconds(
                                                  variable.measurement,
                                                  variable.unit,
                                                ),
                                              )
                                            : ""))
                                        : (variable.measurement ?? "")
                                  }
                                  onChange={(e) => {
                                    if (isTimeVariable(variable.name)) {
                                      const filtered = e.target.value.replace(
                                        /[^0-9:.]/g,
                                        "",
                                      );
                                      handleTimeDisplayChange(index, filtered);
                                    } else if (isRestVariable(variable.name)) {
                                      const filtered = e.target.value.replace(
                                        /[^0-9:.]/g,
                                        "",
                                      );
                                      handleRestDisplayChange(index, filtered);
                                    } else {
                                      const value =
                                        e.target.value === ""
                                          ? null
                                          : parseFloat(e.target.value);
                                      handleVariableChange(index, value);
                                    }
                                  }}
                                  onBlur={() => {
                                    if (isTimeVariable(variable.name)) {
                                      handleTimeDisplayChange(
                                        index,
                                        secondsToTimeDisplay(
                                          timeDisplayToSeconds(
                                            timeDisplayValues[index] ?? "",
                                          ),
                                        ),
                                      );
                                    } else if (isRestVariable(variable.name)) {
                                      handleRestDisplayChange(
                                        index,
                                        secondsToTimeDisplay(
                                          timeDisplayToSeconds(
                                            timeDisplayValues[index] ?? "",
                                          ),
                                        ),
                                      );
                                    }
                                  }}
                                  placeholder={
                                    isTimeVariable(variable.name) ||
                                    isRestVariable(variable.name)
                                      ? "0:00.00"
                                      : "0"
                                  }
                                  inputMode="decimal"
                                  className="w-full input text-[16px] text-right font-geist tabular-nums sm:text-sm focus:outline-none font-bold focus:ring-0 focus:border-transparent p-3 rounded-md bg-base-200 disabled:cursor-not-allowed"
                                  style={{
                                    MozAppearance: "textfield",
                                    WebkitAppearance: "none",
                                    margin: 0,
                                    paddingRight:
                                      isTimeVariable(variable.name) ||
                                      isRestVariable(variable.name)
                                        ? undefined
                                        : "80px",
                                  }}
                                  onWheel={(e) => e.currentTarget.blur()}
                                />

                                {/* Unit Selector - hidden for Time/Rest, which use the dynamic field above instead */}
                                {!isTimeVariable(variable.name) &&
                                  !isRestVariable(variable.name) && (
                                    <div
                                      className={`absolute right-1 top-1/2 transform -translate-y-1/2 ${
                                        activeUnitDropdown ===
                                        `variable-${index}`
                                          ? "z-[100]"
                                          : "z-10"
                                      }`}
                                      style={{ width: "60px" }}
                                    >
                                      <input
                                        type="text"
                                        disabled={!checked}
                                        value={currentUnit || ""}
                                        onChange={(e) => {
                                          handleUnitChange(
                                            index,
                                            e.target.value,
                                          );
                                        }}
                                        onFocus={() =>
                                          setActiveUnitDropdown(
                                            `variable-${index}`,
                                          )
                                        }
                                        onBlur={() => {
                                          setTimeout(
                                            () => setActiveUnitDropdown(null),
                                            200,
                                          );
                                        }}
                                        placeholder="unit"
                                        className="w-full text-[16px] sm:text-xs px-2 py-1 border border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-300 bg-base-100 font-grotesk text-center disabled:cursor-not-allowed"
                                      />

                                      {/* Dropdown menu */}
                                      {activeUnitDropdown ===
                                        `variable-${index}` &&
                                        availableUnits.length > 0 && (
                                          <div className="absolute z-[99999] w-32 mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg max-h-40 overflow-y-auto right-0">
                                            {availableUnits.map((unit) => (
                                              <button
                                                key={unit}
                                                type="button"
                                                onClick={() => {
                                                  handleUnitChange(
                                                    index,
                                                    unit,
                                                  );
                                                  setActiveUnitDropdown(null);
                                                }}
                                                className="w-full text-left px-3 py-1.5 text-xs hover:bg-base-200 focus:bg-base-200 focus:outline-none font-grotesk"
                                              >
                                                {unit}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Tags: Personal Best, Season's Best, Testing */}
                <div className="px-6 pt-2 pb-3">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPersonalBest}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsPersonalBest(checked);
                          if (!checked) {
                            // Stick until the measurement/activity changes.
                            pbDismissedRef.current = true;
                            autoFlaggedPbRef.current = false;
                          } else {
                            pbDismissedRef.current = false;
                          }
                        }}
                        className="checkbox checkbox-sm bg-base-100 border-2 border-base-300 checked:bg-neutral checked:border-base-content"
                      />
                      <span className="text-sm font-sans font-bold text-base-content">
                        Personal Best{" "}
                        <span className="font-light bg-base-200 p-0.5 px-1.5 rounded-md text-xs">
                          PB
                        </span>
                      </span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isSeasonsBest}
                        onChange={(e) => setIsSeasonsBest(e.target.checked)}
                        className="checkbox checkbox-sm bg-base-100 border-2 border-base-300 checked:bg-neutral checked:border-base-content"
                      />
                      <span className="text-sm font-sans font-bold text-base-content">
                        Season's Best{" "}
                        <span className="font-light bg-base-200 p-0.5 px-1.5 rounded-md text-xs">
                          SB
                        </span>
                      </span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isTesting}
                        onChange={(e) => setIsTesting(e.target.checked)}
                        className="checkbox checkbox-sm bg-base-100 border-2 border-base-300 checked:bg-neutral checked:border-base-content"
                      />
                      <span className="text-sm font-sans font-bold text-base-content">
                        Testing
                      </span>
                    </label>
                  </div>
                </div>

                {/* PB Predictor insight (Premium+) */}
                {personalBestEvaluation && (
                  <div className="px-6 pb-3 -mt-1">
                    <div
                      className={`rounded-md p-3 text-sm border ${
                        personalBestEvaluation.isNewPersonalBest
                          ? "bg-primary/10 border-primary/30 text-primary"
                          : "bg-base-200 border-base-300 text-base-content/80"
                      }`}
                    >
                      <p className="font-bold font-sans">
                        {personalBestEvaluation.isNewPersonalBest
                          ? `New Personal Best for ${personalBestEvaluation.variableName}!`
                          : "PB Predictor"}
                      </p>
                      {personalBestEvaluation.previousBest !== null && (
                        <p className="text-xs mt-1 opacity-80 font-sans">
                          Previous best: {personalBestEvaluation.previousBest.toFixed(2)}
                        </p>
                      )}
                      {personalBestEvaluation.predictedNextBest !== null && (
                        <p className="text-xs mt-1 opacity-80 font-sans">
                          Based on your recent trend, your next{" "}
                          {personalBestEvaluation.variableName.toLowerCase()} is
                          projected around{" "}
                          {personalBestEvaluation.predictedNextBest.toFixed(2)}.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {!isPremium && hasMeasurementToEvaluate && (
                  <div className="px-6 pb-3 -mt-1">
                    <div className="flex items-center gap-2 rounded-md p-3 text-xs bg-base-200 border border-base-300 text-base-content/50">
                      <HiLockClosed className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-sans">
                        PB Predictor is a Premium feature — automatically detect
                        personal bests and see your projected next result.
                      </span>
                    </div>
                  </div>
                )}

                {/* Performance Rating */}
                <div className="px-6 pt-2 pb-3">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs font-medium text-base-content/80">
                      Performance rating
                    </p>
                  </div>
                  <div className="bg-base-200 p-3 rounded-md mb-2">
                    <p className="text-xs text-base-content/70">
                      Rate this performance out of 10
                    </p>
                    <p className="text-md text-base-content font-bold">
                      {getPerformanceResponse(performanceExpectation)}
                    </p>
                  </div>
                  <div className="flex space-x-2 items-center">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={performanceExpectation}
                      onChange={(e) =>
                        setPerformanceExpectation(Number(e.target.value))
                      }
                      className="tl-slider w-full"
                      step="1"
                      style={{
                        background: getSliderBackground(performanceExpectation),
                      }}
                    />
                    <span
                      className="font-geist text-base font-bold tabular-nums px-2"
                      style={{
                        color: getPerformanceColor(performanceExpectation),
                      }}
                    >
                      {performanceExpectation}
                    </span>
                  </div>
                </div>

                {/* Notes */}
                <div className="px-6 mt-2 pb-3">
                  <textarea
                    id="performanceNotes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add your performance notes here..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-md bg-base-200 border border-transparent focus:border-base-300 h-28 resize-none focus:outline-none focus:ring-0 text-[16px] placeholder:text-base-content/40"
                  />
                </div>
              </div>

              {/* Footer — floats over the scrolling content with a base→transparent
                  fade so it reads cleanly in light and dark mode. */}
              <div className="px-5 pb-5 pt-28 -mt-24 flex-shrink-0 relative z-50 flex items-center justify-between gap-3 bg-gradient-to-t from-base-100 from-30% to-transparent pointer-events-none">
                {/* Left: delete action (edit mode), in line with the submit button */}
                <div className="flex items-center gap-2 min-w-0 pointer-events-auto">
                  {editMode && onDelete && (
                    <button
                      type="button"
                      onClick={onDelete}
                      disabled={isSubmitting}
                      aria-label="Delete performance"
                      title="Delete performance"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-500/15 text-red-600 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <HiTrash className="w-5 h-5" />
                      Delete
                    </button>
                  )}
                </div>

                {/* Right: circular tick submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!selectedActivityType || isSubmitting}
                  aria-label={editMode ? "Save changes" : "Add performance"}
                  title={editMode ? "Save changes" : "Add performance"}
                  className="pointer-events-auto w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <HiCheck className="w-6 h-6" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Information Dialog */}
      {showInfoDialog && (
        <div
          className="fixed inset-0 z-[10000] bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowInfoDialog(false)}
        >
          <div
            className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold font-grotesk text-base-content">
                  <span className="font-light">Tracking </span> Performance
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 hover:bg-base-200 rounded-full transition-colors"
                >
                  <HiX className="w-5 h-5 text-base-content/60" />
                </button>
              </div>
              <div className="space-y-4 text-sm text-base-content/80">
                <div className="">
                  <p className="text-lg text-base-content/80 font-grotesk">
                    Performance tracking creates a personal history that links
                    preparation, mindset, and results. Used over time, it
                    supports reflection, learning, and more informed
                    conversations with coaches about what is working and why.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Performance Expectations
                  </h4>
                  <p className="font-inter">
                    Setting performance expectations before a race or key
                    session helps you evaluate your training effectiveness and
                    mental preparation. It creates a baseline for measuring
                    progress and identifying areas for improvement.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Actual Results
                  </h4>
                  <p className="font-inter">
                    Recording your actual performance results allows you to
                    compare expectations with reality. This comparison reveals
                    whether you're being too conservative, too ambitious, or
                    right on target with your goals.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Performance Trends
                  </h4>
                  <p className="font-inter">
                    Tracking performance over time builds a clear picture of
                    progression and consistency. This history feeds into the PB
                    Predictor, which captures the circumstances around your best
                    performances to identify conditions that tend to support
                    them, helping build confidence when similar situations arise
                    again.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Mental Game Insights
                  </h4>
                  <p className="font-inter">
                    Comparing expectations to results can reveal patterns in
                    your mental approach. Consistently underperforming
                    expectations might indicate mental barriers, while exceeding
                    them shows growing confidence and capability.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-base-content mb-1 font-inter">
                    Training Validation
                  </h4>
                  <p className="font-inter">
                    Performance logs validate whether your training is working.
                    Improving results confirm your approach is effective, while
                    stagnant or declining performance signals the need for
                    adjustments.
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="w-full bg-neutral text-white py-3 px-6 font-grotesk font-bold rounded-md hover:bg-neutral transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Activity Type Dialog */}
      <CreateActivityTypeDialog
        isOpen={isCreateActivityDialogOpen}
        onClose={() => setIsCreateActivityDialogOpen(false)}
        initialName={searchTerm}
        onActivityTypeCreated={handleActivityTypeCreated}
      />

      {/* Create Training Log Form */}
      {showCreateTrainingLogForm && (
        <TrainingLogForm
          open={showCreateTrainingLogForm}
          onClose={() => setShowCreateTrainingLogForm(false)}
          context={{ dateID }}
          onSaved={
            postSubmitStep
              ? handlePostSubmitCreateTrainingLog
              : handleCreateTrainingLogSubmit
          }
        />
      )}
    </div>
  );
};

export default AddPerformanceLogForm;
